const AUTHORING_CONTEXT_INDEX_URL = "/assistant-context/assistant-context.index.json";
const AUTHORING_CONTEXT_SUMMARY_URL = "/assistant-context/assistant-context.summary.md";
const MAX_RUNTIME_SIGNATURES = 90;
const MAX_MATCHED_PROPERTY_DOCS = 20;
const MAX_CONTEXT_SUMMARY_CHARS = 1800;
const MAX_CURRENT_SCRIPT_CHARS = 12000;
const MAX_ACTIVITY_SCRIPT_CONTEXT_CHARS = 7000;
const MAX_PROMPT_CHARS = 26000;

export const DEFAULT_ASSISTANT_CHAT_MODEL = "gpt-4-turbo";
export const SCRIPT_ASSISTANT_MODE_FUNCTIONS = "script_functions";
export const SCRIPT_ASSISTANT_MODE_VALUE_TRIGGER = "script_value_trigger";
export const DRAFT_ASSISTANT_MODE_FIELD_TEXT = "field_text";
export const DRAFT_RESPONSE_FORMAT_CANDIDATES = "candidates";
export const DRAFT_RESPONSE_FORMAT_MARKDOWN_BULLETS = "markdown_bullets";

let cachedAuthoringContextIndex = null;
let cachedAuthoringContextSummary = "";
let cachedAuthoringContextLoadPromise = null;

function safeArray(value) {
	return Array.isArray(value) ? value : [];
}

function cleanContextText(text, maxChars = 240) {
	if (!text || typeof text !== "string") {
		return "";
	}
	const cleaned = text.replace(/\s+/g, " ").trim();
	return cleaned.length > maxChars ? `${cleaned.slice(0, maxChars)}...` : cleaned;
}

function clipMultilineText(text, maxChars) {
	if (!text || typeof text !== "string") {
		return "";
	}
	if (text.length <= maxChars) {
		return text;
	}
	const headChars = Math.max(0, Math.floor((maxChars - 48) / 2));
	const tailChars = Math.max(0, maxChars - headChars - 48);
	return `${text.slice(0, headChars)}\n/* ... script truncated for prompt size ... */\n${text.slice(text.length - tailChars)}`;
}

function toLocaleDateLabel(isoValue = "") {
	if (!isoValue) {
		return "";
	}
	try {
		return new Date(isoValue).toLocaleString();
	} catch (_err) {
		return isoValue;
	}
}

function getTokenSet(message = "") {
	const tokens = ((message || "").toLowerCase().match(/[a-z0-9_/-]{2,}/g) || []);
	return new Set(tokens);
}

function getMatchedPropertyDocs(index, question = "") {
	if (!index || !index.propertyDocs || !index.propertyDocs.byScope) {
		return [];
	}

	const tokenSet = getTokenSet(question);
	const questionLower = (question || "").toLowerCase();
	const docs = [];
	const byScope = index.propertyDocs.byScope || {};

	Object.keys(byScope).forEach((scope) => {
		const scopedProperties = safeArray(byScope[scope] && byScope[scope].properties);
		scopedProperties.forEach((doc) => {
			docs.push({
				scope,
				key: doc && doc.key ? doc.key : "",
				name: doc && doc.name ? doc.name : "",
				description: doc && doc.description ? doc.description : ""
			});
		});
	});

	return docs
		.map((doc) => {
			const key = doc.key.toLowerCase();
			const name = doc.name.toLowerCase();
			const description = doc.description.toLowerCase();
			let score = 0;

			if (key && questionLower.indexOf(key) > -1) {
				score += 12;
			}
			if (name && questionLower.indexOf(name) > -1) {
				score += 8;
			}

			for (const token of tokenSet) {
				if (token.length < 3) {
					continue;
				}
				if (key === token || name === token) {
					score += 6;
				} else if (key.indexOf(token) > -1 || name.indexOf(token) > -1) {
					score += 4;
				}
				if (description.indexOf(token) > -1) {
					score += 1;
				}
			}

			return {
				...doc,
				score
			};
		})
		.filter((doc) => doc.score > 0)
		.sort((a, b) => {
			if (b.score !== a.score) {
				return b.score - a.score;
			}
			if (a.scope !== b.scope) {
				return a.scope.localeCompare(b.scope);
			}
			return a.key.localeCompare(b.key);
		})
		.slice(0, MAX_MATCHED_PROPERTY_DOCS);
}

function buildRuntimeMethodMap(index) {
	const runtimeApi = index && index.runtimeApi ? index.runtimeApi : {};
	const map = {};

	["web", "mobile"].forEach((platform) => {
		safeArray(runtimeApi[platform]).forEach((method) => {
			if (!method || !method.name) {
				return;
			}

			const name = method.name;
			const params = safeArray(method.params);
			const current = map[name] || {
				name,
				params: [],
				async: false,
				platforms: []
			};

			if (params.length > current.params.length) {
				current.params = params;
			}
			current.async = current.async || !!method.async;
			if (current.platforms.indexOf(platform) === -1) {
				current.platforms.push(platform);
			}

			map[name] = current;
		});
	});

	return map;
}

function selectRuntimeMethods(index, question = "") {
	const methodMap = buildRuntimeMethodMap(index);
	const allNames = Object.keys(methodMap).sort((a, b) => a.localeCompare(b));
	if (allNames.length === 0) {
		return [];
	}

	const tokenSet = getTokenSet(question);
	const lowerQuestion = (question || "").toLowerCase();
	const runtimeParity = index && index.runtimeApiParity ? index.runtimeApiParity : {};
	const commonMethods = safeArray(runtimeParity.commonMethods);
	const webOnlyMethods = safeArray(runtimeParity.webOnlyMethods);
	const mobileOnlyMethods = safeArray(runtimeParity.mobileOnlyMethods);

	const matchedMethods = allNames.filter((name) => {
		const lowerName = name.toLowerCase();
		if (lowerQuestion.indexOf(lowerName) > -1) {
			return true;
		}
		for (const token of tokenSet) {
			if (token.length < 3) {
				continue;
			}
			if (lowerName.indexOf(token) > -1 || token.indexOf(lowerName) > -1) {
				return true;
			}
		}
		return false;
	});

	const orderedNames = [];
	const seen = new Set();
	const pushUnique = (name) => {
		if (!name || seen.has(name) || !methodMap[name]) {
			return;
		}
		seen.add(name);
		orderedNames.push(name);
	};

	matchedMethods.forEach(pushUnique);
	commonMethods.forEach(pushUnique);
	webOnlyMethods.forEach(pushUnique);
	mobileOnlyMethods.forEach(pushUnique);
	allNames.forEach(pushUnique);

	return orderedNames.slice(0, MAX_RUNTIME_SIGNATURES).map((name) => methodMap[name]);
}

function formatRuntimeMethodSignature(method) {
	if (!method || !method.name) {
		return "";
	}
	const params = safeArray(method.params).join(", ");
	const asyncPrefix = method.async ? "async " : "";
	const platforms = safeArray(method.platforms);
	const platformLabel = platforms.length === 2
		? "web+mobile"
		: (platforms[0] || "unknown");
	return `${asyncPrefix}af.${method.name}(${params}) [${platformLabel}]`;
}

function getModeConfig(mode) {
	if (mode === SCRIPT_ASSISTANT_MODE_VALUE_TRIGGER) {
		return {
			title: "Value Trigger Script",
			scopeLines: [
				"This script runs in an item's Value Trigger context.",
				"Favor guard clauses and avoid write loops when calling af.setValue."
			],
			overrideKey: "valueTriggerPromptDirectives"
		};
	}

	return {
		title: "Functions Script",
		scopeLines: [
			"This script runs in the activity-level Functions context.",
			"Favor reusable helpers and avoid side effects during initialization."
		],
		overrideKey: "functionsPromptDirectives"
	};
}

function trimPrompt(text) {
	if (!text || text.length <= MAX_PROMPT_CHARS) {
		return text;
	}
	return `${text.slice(0, MAX_PROMPT_CHARS)}\n\n[Context truncated for prompt size.]`;
}

function extractRequestedBulletCount(question = "") {
	const raw = (question || "").toLowerCase();
	if (!raw) {
		return null;
	}

	const wordToNumber = {
		one: 1,
		two: 2,
		three: 3,
		four: 4,
		five: 5,
		six: 6,
		seven: 7,
		eight: 8,
		nine: 9,
		ten: 10
	};

	const digitMatch = raw.match(/\b(?:exactly\s+)?(\d{1,2})\s+(?:markdown\s+)?bullets?\b/);
	if (digitMatch && digitMatch[1]) {
		const value = parseInt(digitMatch[1], 10);
		if (!Number.isNaN(value) && value > 0 && value <= 20) {
			return value;
		}
	}

	const wordMatch = raw.match(/\b(?:exactly\s+)?(one|two|three|four|five|six|seven|eight|nine|ten)\s+(?:markdown\s+)?bullets?\b/);
	if (wordMatch && wordMatch[1] && wordToNumber[wordMatch[1]]) {
		return wordToNumber[wordMatch[1]];
	}

	return null;
}

export async function loadAuthoringAssistantContext({ forceRefresh = false } = {}) {
	if (forceRefresh) {
		cachedAuthoringContextIndex = null;
		cachedAuthoringContextSummary = "";
		cachedAuthoringContextLoadPromise = null;
	}

	if (cachedAuthoringContextIndex || cachedAuthoringContextSummary) {
		return {
			ok: true,
			status: cachedAuthoringContextIndex ? "index" : "summary",
			index: cachedAuthoringContextIndex,
			summary: cachedAuthoringContextSummary,
			generatedAtIso: cachedAuthoringContextIndex && cachedAuthoringContextIndex.meta ? cachedAuthoringContextIndex.meta.generatedAtIso : "",
			generatedAtLabel: toLocaleDateLabel(cachedAuthoringContextIndex && cachedAuthoringContextIndex.meta ? cachedAuthoringContextIndex.meta.generatedAtIso : ""),
			error: ""
		};
	}

	if (cachedAuthoringContextLoadPromise) {
		return cachedAuthoringContextLoadPromise;
	}

	cachedAuthoringContextLoadPromise = (async () => {
		try {
			const response = await fetch(AUTHORING_CONTEXT_INDEX_URL, {
				method: "GET",
				cache: "no-cache"
			});
			if (response.ok) {
				cachedAuthoringContextIndex = await response.json();
				const generatedAtIso = cachedAuthoringContextIndex && cachedAuthoringContextIndex.meta
					? cachedAuthoringContextIndex.meta.generatedAtIso
					: "";
				return {
					ok: true,
					status: "index",
					index: cachedAuthoringContextIndex,
					summary: cachedAuthoringContextSummary,
					generatedAtIso,
					generatedAtLabel: toLocaleDateLabel(generatedAtIso),
					error: ""
				};
			}
		} catch (_err) {
			// Fallback to summary lookup.
		}

		try {
			const summaryResponse = await fetch(AUTHORING_CONTEXT_SUMMARY_URL, {
				method: "GET",
				cache: "no-cache"
			});
			if (summaryResponse.ok) {
				cachedAuthoringContextSummary = await summaryResponse.text();
				return {
					ok: true,
					status: "summary",
					index: null,
					summary: cachedAuthoringContextSummary,
					generatedAtIso: "",
					generatedAtLabel: "",
					error: ""
				};
			}
		} catch (_err) {
			// Continue to missing context response.
		}

		return {
			ok: false,
			status: "missing",
			index: null,
			summary: "",
			generatedAtIso: "",
			generatedAtLabel: "",
			error: "Assistant context was not found at /assistant-context. Run the assistant-context generator to publish artifacts."
		};
	})();

	try {
		return await cachedAuthoringContextLoadPromise;
	} finally {
		cachedAuthoringContextLoadPromise = null;
	}
}

export function buildScriptAssistantPrompt({
	mode = SCRIPT_ASSISTANT_MODE_FUNCTIONS,
	question = "",
	currentScript = "",
	activityScriptingContext = "",
	featureName = "",
	featureDescription = "",
	contextIndex = null,
	contextSummary = "",
	additionalContextLines = []
} = {}) {
	const config = getModeConfig(mode);
	const overrides = contextIndex && contextIndex.overrides ? contextIndex.overrides : {};
	const runtimeParity = contextIndex && contextIndex.runtimeApiParity ? contextIndex.runtimeApiParity : {};
	const modeDirectives = safeArray(overrides[config.overrideKey]);
	const sharedDirectives = safeArray(overrides.scriptingPromptDirectives);
	const knownCorrections = safeArray(overrides.knownCorrections);
	const hierarchyRules = safeArray(overrides.authoringHierarchyRules);

	const sections = [];
	sections.push([
		"You are the AmbiFi Scripting Assistant for authors.",
		"Generate practical JavaScript that works in AmbiFi runtime.",
		"Support both scripting Q&A and code generation."
	].join("\n"));

	sections.push([
		"Hard constraints:",
		"- If code is requested, return JavaScript only (no TypeScript).",
		"- Do not use template literals/backticks for strings.",
		"- Prefix AmbiFi runtime API calls with af. (for example, af.getValue(id)).",
		"- Prefer idempotent, defensive code with null checks.",
		"- Mention web/mobile differences if APIs are not parity-safe."
	].join("\n"));

	sections.push([
		`Scripting mode: ${config.title}`,
		...config.scopeLines
	].join("\n"));

	if (featureName || featureDescription) {
		sections.push([
			"Panel context:",
			`- Property: ${featureName || "unknown"}`,
			`- Description: ${featureDescription || "n/a"}`
		].join("\n"));
	}

	if (safeArray(additionalContextLines).length > 0) {
		sections.push([
			"Additional context:",
			...safeArray(additionalContextLines).map((line) => `- ${cleanContextText(line, 240)}`)
		].join("\n"));
	}

	if (sharedDirectives.length > 0 || modeDirectives.length > 0 || hierarchyRules.length > 0 || knownCorrections.length > 0) {
		const overrideLines = ["Authoritative manual overrides:"];
		sharedDirectives.forEach((directive) => {
			overrideLines.push(`- ${cleanContextText(directive, 240)}`);
		});
		modeDirectives.forEach((directive) => {
			overrideLines.push(`- ${cleanContextText(directive, 240)}`);
		});
		hierarchyRules.slice(0, 12).forEach((rule) => {
			overrideLines.push(`- ${cleanContextText(rule, 240)}`);
		});
		knownCorrections.slice(0, 12).forEach((correction) => {
			const topic = correction && correction.topic ? `${cleanContextText(correction.topic, 80)}: ` : "";
			const correct = correction && correction.correct ? cleanContextText(correction.correct, 220) : "";
			if (correct) {
				overrideLines.push(`- ${topic}${correct}`);
			}
		});
		sections.push(overrideLines.join("\n"));
	}

	if (contextIndex) {
		const meta = contextIndex.meta || {};
		sections.push([
			"Context snapshot:",
			`- Generated: ${meta.generatedAtIso || "unknown"}`,
			`- Fingerprint: ${meta.sourceFingerprint || "unknown"}`,
			`- Runtime parity web/mobile: ${runtimeParity.parityPercent || 0}%`,
			`- Common runtime methods: ${runtimeParity.commonMethodCount || 0}`,
			`- Web-only runtime methods: ${safeArray(runtimeParity.webOnlyMethods).length}`,
			`- Mobile-only runtime methods: ${safeArray(runtimeParity.mobileOnlyMethods).length}`
		].join("\n"));

		const runtimeMethods = selectRuntimeMethods(contextIndex, question);
		if (runtimeMethods.length > 0) {
			sections.push([
				"Runtime API signatures:",
				...runtimeMethods.map((method) => `- ${formatRuntimeMethodSignature(method)}`)
			].join("\n"));
		}

		const matchedPropertyDocs = getMatchedPropertyDocs(contextIndex, question);
		if (matchedPropertyDocs.length > 0) {
			sections.push([
				"Relevant documented properties:",
				...matchedPropertyDocs.map((doc) => `- ${doc.scope}.${doc.key}: ${cleanContextText(doc.description || doc.name, 220)}`)
			].join("\n"));
		}
	} else if (contextSummary) {
		sections.push([
			"Context summary excerpt:",
			cleanContextText(contextSummary, MAX_CONTEXT_SUMMARY_CHARS)
		].join("\n"));
	}

	if (activityScriptingContext && typeof activityScriptingContext === "string" && activityScriptingContext.trim() !== "") {
		sections.push([
			"Activity scripting context (functions + value triggers):",
			clipMultilineText(activityScriptingContext, MAX_ACTIVITY_SCRIPT_CONTEXT_CHARS)
		].join("\n"));
	}

	sections.push([
		"Current script:",
		clipMultilineText(currentScript || "// (empty)", MAX_CURRENT_SCRIPT_CHARS)
	].join("\n"));

	sections.push(`Author question:\n${question || ""}`);

	sections.push([
		"Response policy:",
		"- If the author asks a conceptual/informational question, answer with concise bullets and practical caveats; code block is optional.",
		"- If the author asks to generate/modify script, respond with:",
		"  1) 1-3 short bullets explaining the approach",
		"  2) one complete ```javascript``` code block ready to paste",
		"  3) a short caveats section",
		"- When code is included, include exactly one fenced javascript block."
	].join("\n"));

	return trimPrompt(sections.join("\n\n"));
}

export function buildDraftAssistantPrompt({
	question = "",
	currentValue = "",
	targetLabel = "",
	targetFieldType = "text_plain",
	featureName = "",
	featureDescription = "",
	responseFormat = DRAFT_RESPONSE_FORMAT_CANDIDATES,
	contextIndex = null,
	contextSummary = "",
	additionalContextLines = []
} = {}) {
	const overrides = contextIndex && contextIndex.overrides ? contextIndex.overrides : {};
	const runtimeParity = contextIndex && contextIndex.runtimeApiParity ? contextIndex.runtimeApiParity : {};
	const sharedDirectives = safeArray(overrides.authoringPromptDirectives);
	const knownCorrections = safeArray(overrides.knownCorrections);
	const hierarchyRules = safeArray(overrides.authoringHierarchyRules);

	const sections = [];
	sections.push([
		"You are the AmbiFi Drafting Assistant for authors.",
		"Help authors create content they can paste into authoring fields.",
		"Prioritize concise, production-ready text options."
	].join("\n"));

	sections.push([
		"Hard constraints:",
		"- Treat the canonical authoring model as player-checklist JSON.",
		"- Use plain, readable language unless the author asks for a specific style.",
		"- Avoid hallucinating unavailable fields or unsupported hierarchy rules.",
		"- If unsure, say what you are inferring."
	].join("\n"));

	const targetLines = [
		"Draft target context:",
		`- Field label: ${targetLabel || "None selected"}`,
		`- Field type: ${targetFieldType || "text_plain"}`,
		`- Property name: ${featureName || "unknown"}`,
		`- Property description: ${featureDescription || "n/a"}`
	];
	sections.push(targetLines.join("\n"));

	if (safeArray(additionalContextLines).length > 0) {
		sections.push([
			"Additional context:",
			...safeArray(additionalContextLines).map((line) => `- ${cleanContextText(line, 240)}`)
		].join("\n"));
	}

	if (sharedDirectives.length > 0 || hierarchyRules.length > 0 || knownCorrections.length > 0) {
		const overrideLines = ["Authoritative manual overrides:"];
		sharedDirectives.slice(0, 20).forEach((directive) => {
			overrideLines.push(`- ${cleanContextText(directive, 240)}`);
		});
		hierarchyRules.slice(0, 20).forEach((rule) => {
			overrideLines.push(`- ${cleanContextText(rule, 240)}`);
		});
		knownCorrections.slice(0, 20).forEach((correction) => {
			const topic = correction && correction.topic ? `${cleanContextText(correction.topic, 80)}: ` : "";
			const correct = correction && correction.correct ? cleanContextText(correction.correct, 220) : "";
			if (correct) {
				overrideLines.push(`- ${topic}${correct}`);
			}
		});
		sections.push(overrideLines.join("\n"));
	}

	if (contextIndex) {
		const meta = contextIndex.meta || {};
		sections.push([
			"Context snapshot:",
			`- Generated: ${meta.generatedAtIso || "unknown"}`,
			`- Fingerprint: ${meta.sourceFingerprint || "unknown"}`,
			`- Runtime parity web/mobile: ${runtimeParity.parityPercent || 0}%`
		].join("\n"));

		const matchedPropertyDocs = getMatchedPropertyDocs(contextIndex, question);
		if (matchedPropertyDocs.length > 0) {
			sections.push([
				"Relevant documented properties:",
				...matchedPropertyDocs.map((doc) => `- ${doc.scope}.${doc.key}: ${cleanContextText(doc.description || doc.name, 220)}`)
			].join("\n"));
		}
	} else if (contextSummary) {
		sections.push([
			"Context summary excerpt:",
			cleanContextText(contextSummary, MAX_CONTEXT_SUMMARY_CHARS)
		].join("\n"));
	}

	sections.push([
		"Current field value:",
		clipMultilineText(currentValue || "(empty)", 1800)
	].join("\n"));

	sections.push(`Author question:\n${question || ""}`);

	if (responseFormat === DRAFT_RESPONSE_FORMAT_MARKDOWN_BULLETS) {
		const requestedBulletCount = extractRequestedBulletCount(question);
		const targetBulletCount = requestedBulletCount || 3;
		sections.push([
			"Respond with a single markdown bullet list.",
			"Format requirements:",
			"- Use '-' for each bullet.",
			`- Return exactly ${targetBulletCount} bullets.`,
			"- No candidate headings.",
			"- No extra prose before or after the list."
		].join("\n"));
	} else {
		sections.push([
			"Respond with 2-4 candidate snippets.",
			"Format:",
			"### Candidate 1",
			"<snippet>",
			"### Candidate 2",
			"<snippet>",
			"Do not include extra prose outside candidate content."
		].join("\n"));
	}

	return trimPrompt(sections.join("\n\n"));
}

export function extractCodeFromAssistantResponse(responseText = "") {
	const raw = typeof responseText === "string" ? responseText.trim() : "";
	if (raw === "") {
		return "";
	}

	const fenced = raw.match(/```(?:javascript|js|json)?\s*([\s\S]*?)```/i);
	if (fenced && fenced[1]) {
		return fenced[1].trim();
	}

	return "";
}
